/* eslint-disable react/jsx-curly-newline */
import React, { useState, useEffect, useCallback } from "react";
import { UseFormReturn, Controller } from "react-hook-form";

// Pages
import { IFormConcretingRequestInputs } from "@pages/ConcretingRequest";

// GraphQL
import {
  EConcretingRequestPump,
  ConcretingRequestType
} from "@store/graphql/helpers/types.graphql";

// Languages
import useTranslation from "@languages/useTranslation";
import { MuiMessages } from "@languages/interfaces";
import { BaseCardMessages } from "src/languages/interfaces";

// Helpers
import { handleErrorMessage, getDateLocale } from "@helpers/index";

// Libraries
import {
  TextField,
  DatePicker,
  FormControl,
  Autocomplete,
  AdapterDateFns,
  FormHelperText,
  CircularProgress,
  LocalizationProvider,
  InputLabel,
  Select,
  SelectProps,
  MenuItem
} from "@libraries/mui";

import * as Styled from "./styles";

interface FormConcretingRequestPumpProps {
  controlForms: UseFormReturn<IFormConcretingRequestInputs>;
  currentConcretingRequest: ConcretingRequestType | undefined;
  onlyView: boolean;
}

interface CustomOptionType {
  id: string;
  value: string;
}

export const pumpOptions: CustomOptionType[] = [
  { id: EConcretingRequestPump.NO_PUMP, value: "Sem bomba" },
  { id: EConcretingRequestPump.STATIONARY, value: "Bomba estacionária" },
  { id: EConcretingRequestPump.BOOM, value: "Bomba lança" }
];

const FormConcretingRequestPump: React.FC<FormConcretingRequestPumpProps> = ({
  controlForms,
  currentConcretingRequest,
  onlyView
}) => {
  const { t } = useTranslation();
  const dateLocale = getDateLocale();

  const {
    control,
    setValue,
    getValues,
    formState: { errors: formErrors },
    watch
  } = controlForms;

  useEffect(() => {
    if (!currentConcretingRequest) return;

    setValue("pump", currentConcretingRequest.pump);
    setValue("observationPump", currentConcretingRequest.observationPump ?? "");
    setValue("pipingDistance", currentConcretingRequest.pipingDistance ?? "");
  }, [currentConcretingRequest, setValue]);

  return (
    <Styled.FormContainer>
      <Controller
        name="pump"
        control={control}
        defaultValue={pumpOptions[0].id}
        render={({ field }) => (
          <Styled.CustomFormControl
            error={!!formErrors.pump}
            className="default-field"
            size="small"
            fullWidth
          >
            <InputLabel id="concrete-type-select">
              {t(BaseCardMessages.concretingRequestPumpDescriptionText)}
            </InputLabel>
            <Select
              {...field}
              labelId="pump-select"
              label={t(BaseCardMessages.concretingRequestPumpDescriptionText)}
              error={!!formErrors.pump}
              // onChange={(e) => handleChange(e.target.value as EConcreteType)}
              disabled={onlyView}
            >
              {pumpOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Styled.SelectItem>{option.value}</Styled.SelectItem>
                </MenuItem>
              ))}
            </Select>
            {!!formErrors.pump && (
              <FormHelperText>
                {handleErrorMessage(formErrors.pump, t)}
              </FormHelperText>
            )}
          </Styled.CustomFormControl>
        )}
      />
      <Controller
        name="pipingDistance"
        control={control}
        defaultValue={0}
        render={({ field }) => (
          <TextField
            {...field}
            type="number"
            autoComplete="off"
            label={t(BaseCardMessages.concretingRequestPipingDistanceText)}
            variant="outlined"
            error={!!formErrors.pipingDistance}
            helperText={handleErrorMessage(formErrors.pipingDistance, t)}
            className="default-field"
            disabled={onlyView}
            size="small"
            InputProps={{
              inputProps: {
                min: 0
              }
            }}
          />
        )}
      />

      <Controller
        name="observationPump"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            type="text"
            autoComplete="off"
            label={t(BaseCardMessages.concretingRequestObservationText)}
            variant="outlined"
            error={!!formErrors.observationPump}
            helperText={handleErrorMessage(formErrors.observationPump, t)}
            className="default-field"
            disabled={onlyView}
            size="small"
            InputProps={{
              inputProps: {
                min: 0
              }
            }}
          />
        )}
      />
    </Styled.FormContainer>
  );
};

export default FormConcretingRequestPump;
